import { Form, FormikProvider, useFormik } from 'formik'
import useMediaQuery from '@mui/material/useMediaQuery'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { Box, DialogActions } from '@mui/material'
import Typography from '@mui/material/Typography'
import { Error, Add, Remove } from '@mui/icons-material'

import { useTheme } from '@mui/material/styles'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from 'react-query'
import Tooltip from '@mui/material/Tooltip'
import React, { useCallback } from 'react'
import Stack from '@mui/material/Stack'
import dayjs from 'dayjs'

import HiddenWholesalerLogo from 'shared/components/HiddenWholesalerLogo'
import ContainedButton from 'shared/components/Buttons/ContainedButton'
import OutlinedButton from 'shared/components/Buttons/OutlinedButton'
import { useCartSummaryQuery } from 'shared/store/useCartSummary'
import { useClientPrices } from 'shared/hooks/ClientPriceHook'
import CustomDialog from 'shared/components/CustomDialog'
import { useProfileQuery } from 'shared/store/useProfile'
import InputField from 'shared/components/InputField'
import { MAX_PRODUCT_QUANTITY } from 'shared/consts'
import { Yup } from 'shared/lib'

import useCustomersQuery from 'shared/store/useCustomers'
import { BuyNowPayload, Modal, ModalFormDefaults, ModalProductDetails, ProductDetails } from '../../types'

const schema = Yup.object().shape({
   cartName: Yup.string().required(),
   clientPrice: Yup.number()
      .required()
      .test({
         name: 'is-proper-value',
         test(value, ctx) {
            const salerPrice = ctx.parent.convertedNetPrice
               ? (ctx.parent.convertedNetPrice as number)
               : (ctx.parent.salerPrice as number)
            if (typeof value === 'number' && value + 0.01 > salerPrice) {
               return true
            }
            return ctx.createError({ message: 'minimumValue', params: { value: salerPrice + 0.01 } })
         },
      }),
   salerPrice: Yup.number().required(),
   quantity: Yup.number()
      .required()
      .test({
         name: 'quantity-in-set',
         test(value, ctx) {
            const quantityInSet = ctx.parent.quantityInSet as number
            if (value === quantityInSet || (typeof value === 'number' && value % quantityInSet === 0)) {
               return true
            }
            return ctx.createError({ message: 'multipleValue', params: { value: quantityInSet } })
         },
      }),
})

interface Props {
   onClose: () => void
   modalData: Modal
   producer?: PropType<ProductDetails, 'producer'>
   partIndex?: PropType<ProductDetails, 'index'>
   onBuy: (payload: BuyNowPayload, wholesaleIndex?: string, productIndex?: string) => void
   onAddToCart: (
      payload: AddToCartPayload,
      wholesaleIndex?: string,
      productIndex?: string
   ) => Promise<{ status: number }>
   isLoading: boolean
}

export default function ModalComponent({
   onClose,
   modalData,
   producer,
   partIndex,
   onBuy,
   onAddToCart,
   isLoading,
}: Props) {
   const { data: profileData } = useProfileQuery()
   const queryClient = useQueryClient()
   const { data: cartData } = useCartSummaryQuery()
   const { t } = useTranslation(['TRANSLATION', 'PRODUCT_DETAILS'])
   const { clientPricesOn } = useClientPrices()
   const { data: customers } = useCustomersQuery()

   const priceType = profileData?.priceType || 'GROSS'
   const theme = useTheme()

   const fullScreen = useMediaQuery(theme.breakpoints.down('lg'))
   const isMobile = useMediaQuery(theme.breakpoints.down('md'))
   const productDetails: ModalProductDetails = {
      index: partIndex || '',
      producer: producer || '',
      wholesaleName: modalData?.data.wholesaleName || '',
      wholesaleUuid: modalData?.data.wholesaleUuid || '',
   }

   function handleOnBuy(values: ModalFormDefaults) {
      const payload: BuyNowPayload = {
         cartName: values.cartName,
         netClientPrice:
            priceType === 'NET'
               ? values.clientPrice.toString()
               : (values.clientPrice / (1 + values.taxRate / 100)).toFixed(2).toString(),
         netPrice: values.netPrice.toString(),
         grossPrice: values.grossPrice.toString(),
         currency: values.currency,
         taxRate: values.taxRate,
         quantity: values.quantity,
         wholesaleUuid: modalData?.data.wholesaleUuid as string,
      }
      onBuy(payload, modalData.data.wholesaleIndex, partIndex)
   }

   function handleOnAddToCart(values: ModalFormDefaults) {
      const payload: AddToCartPayload = {
         netClientPrice:
            priceType === 'NET'
               ? values.clientPrice.toString()
               : (values.clientPrice / (1 + values.taxRate / 100)).toFixed(2).toString(),
         netPrice: values.netPrice.toString(),
         grossPrice: values.grossPrice.toString(),
         currency: values.currency,
         taxRate: values.taxRate,
         quantity: values.quantity,
         wholesaleUuid: modalData?.data.wholesaleUuid as string,
      }
      onAddToCart(payload, modalData.data.wholesaleIndex, partIndex).then((res) => {
         if (res.status === 200) {
            queryClient.invalidateQueries('mainCart')
            onClose()
         }
      })
   }
   const taxRate = modalData.data.taxRate || 23
   const netPrice = modalData.data.netPrice || 0
   const convertedNetPrice = modalData.data.convertedNetPrice || 0
   const totalAvailability = modalData.data.totalAvailability || 0
   const quantityInSet = modalData.data.quantityInSet || 1
   const grossPrice = modalData.data.grossPrice || 0

   function calculateClientPrice() {
      if (cartData?.cartDetailsDTO.clientUuid) {
         const customer = customers?.find((c) => c.uuid === cartData.cartDetailsDTO.clientUuid)
         if (customer?.productsMarkupSettings.priceType) {
            if (customer.productsMarkupSettings.priceType === 'NET') {
               const threshold =
                  netPrice >
                  customer.productsMarkupSettings.thresholds[customer.productsMarkupSettings.thresholds.length - 1]
                     .start
                     ? customer.productsMarkupSettings.thresholds[customer.productsMarkupSettings.thresholds.length - 1]
                     : customer.productsMarkupSettings.thresholds
                          .slice(0, -1)
                          .find((thresh) => netPrice > thresh.start && netPrice <= thresh.end!)
               return threshold
                  ? parseFloat((netPrice * threshold.multiplier + threshold.addend).toFixed(2))
                  : parseFloat(netPrice.toFixed(2))
            }
            const threshold =
               grossPrice >
               customer.productsMarkupSettings.thresholds[customer.productsMarkupSettings.thresholds.length - 1].start
                  ? customer.productsMarkupSettings.thresholds[customer.productsMarkupSettings.thresholds.length - 1]
                  : customer.productsMarkupSettings.thresholds
                       .slice(0, -1)
                       .find((thresh) => grossPrice > thresh.start && grossPrice <= thresh.end!)
            return threshold
               ? parseFloat((grossPrice * threshold.multiplier + threshold.addend).toFixed(2))
               : parseFloat(grossPrice.toFixed(2))
         }
      }
      return priceType === 'NET' ? modalData.data.clientNetPrice : modalData.data.clientGrossPrice
   }

   const salerPrice = priceType === 'NET' ? netPrice.toString() : grossPrice.toString()
   const infinityThreshold = modalData.data.wholesaleInfiniteAvailabilityThreshold || MAX_PRODUCT_QUANTITY
   const currency = modalData.data.currency || 'PLN'
   const clientPrice = calculateClientPrice()
   const isBuyForm = modalData.type === 'BUY'
   const isInCart = cartData?.products.find((item) => item.productEntityUuid === modalData.data.productUuid)

   const formik = useFormik<ModalFormDefaults & { cartName: string }>({
      initialValues: {
         clientPrice: clientPrice || 0,
         grossPrice,
         currency,
         taxRate,
         salerPrice,
         convertedNetPrice,
         netPrice,
         quantityInSet,
         quantity: quantityInSet || 1,
         cartName: dayjs().format('DD-MM-YYYY HH:mm:ss'),
      },
      validationSchema: schema,
      onSubmit: isBuyForm ? handleOnBuy : handleOnAddToCart,
   })

   const { setFieldValue, values: formikValues, isValid } = formik

   const renderForm = useCallback(() => {
      if (!modalData) {
         return null
      }

      if (modalData.type) {
         return (
            <DialogContent>
               <Form>
                  {isBuyForm && (
                     <InputField
                        sx={{ marginBottom: 3 }}
                        inputProps={{
                           maxLength: 50,
                        }}
                        variant="standard"
                        fullWidth
                        type="text"
                        name="cartName"
                        label={t('TRANSLATION:columnNames.cartName')}
                     />
                  )}
                  <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="space-between">
                     <div style={{ flex: 1 }}>
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              pt: { lg: '30px', md: '30px', xs: '15px' },
                           }}
                        >
                           <Box sx={{ marginBottom: { lg: '55px', md: '55px', xs: '25px' } }}>
                              <HiddenWholesalerLogo
                                 wholesalerUuid={productDetails.wholesaleUuid}
                                 wholesalerName={productDetails.wholesaleName}
                                 imgHeight="auto"
                                 imgWidth="160px"
                              />
                           </Box>
                           <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                              <div style={{ display: 'flex', gap: 2 }}>
                                 <Typography variant="body1" color="text.secondary">
                                    {`${t('TRANSLATION:columnNames.partIndex')}: `}
                                 </Typography>
                                 <Typography variant="body1">{productDetails?.index}</Typography>
                              </div>
                              <div style={{ display: 'flex', gap: 2 }}>
                                 <Typography variant="body1" color="text.secondary">
                                    {`${t('TRANSLATION:columnNames.producer')}: `}
                                 </Typography>
                                 <Typography variant="body1">{productDetails.producer}</Typography>
                              </div>
                           </div>
                        </Box>
                     </div>
                     <div style={{ flex: 1 }}>
                        {!fullScreen ? (
                           <Box sx={{ marginBottom: 3, marginTop: 2 }}>
                              <InputField
                                 fullWidth
                                 variant="standard"
                                 type="Number"
                                 name="quantity"
                                 label={t('TRANSLATION:columnNames.pickQuantity')}
                                 InputProps={{
                                    inputProps: {
                                       min: quantityInSet,
                                       max:
                                          totalAvailability < infinityThreshold
                                             ? totalAvailability
                                             : MAX_PRODUCT_QUANTITY,
                                       step: quantityInSet,
                                    },
                                 }}
                              />
                           </Box>
                        ) : (
                           <Box
                              sx={{
                                 marginBottom: 3,
                                 marginTop: 2,
                                 display: 'flex',
                                 justifyContent: 'center',
                                 alignItems: 'center',
                                 gap: '24px',
                              }}
                           >
                              <ContainedButton
                                 variant="contained"
                                 disabled={formikValues.quantity === 1}
                                 onClick={() =>
                                    setFieldValue('quantity', formikValues.quantity - formikValues.quantityInSet)
                                 }
                              >
                                 <Remove />
                              </ContainedButton>
                              <InputField
                                 sx={{
                                    width: '100px',
                                 }}
                                 variant="standard"
                                 type="Number"
                                 name="quantity"
                                 label={t('TRANSLATION:columnNames.pickQuantity')}
                                 InputProps={{
                                    inputProps: {
                                       min: quantityInSet,
                                       max:
                                          totalAvailability < infinityThreshold
                                             ? totalAvailability
                                             : MAX_PRODUCT_QUANTITY,
                                       step: quantityInSet,
                                    },
                                 }}
                              />
                              <ContainedButton
                                 variant="contained"
                                 onClick={() =>
                                    setFieldValue('quantity', formikValues.quantity + formikValues.quantityInSet)
                                 }
                              >
                                 <Add />
                              </ContainedButton>
                           </Box>
                        )}
                        {!clientPricesOn && (
                           <InputField
                              sx={{ marginBottom: 3 }}
                              fullWidth
                              variant="standard"
                              name={convertedNetPrice ? 'convertedNetPrice' : 'salerPrice'}
                              label={`${t('TRANSLATION:columnNames.price')} (${convertedNetPrice ? 'EUR' : currency})`}
                              disabled
                           />
                        )}
                        <InputField
                           inputProps={{
                              step: 0.01,
                           }}
                           onFocus={(e) => e.target.select()}
                           fullWidth
                           variant="standard"
                           type="Number"
                           name="clientPrice"
                           label={`${t('PRODUCT_DETAILS:modals.setClientPrice')} (${
                              convertedNetPrice ? 'EUR' : currency
                           })`}
                        />
                        {!isBuyForm && isInCart && (
                           <Typography variant="h6" sx={{ mt: 3 }}>
                              {t(['PRODUCT_DETAILS:modals.alreadyInCart'])}
                           </Typography>
                        )}
                     </div>
                  </Stack>
               </Form>
            </DialogContent>
         )
      }
      return null
   }, [modalData, isLoading, formikValues])

   return (
      <CustomDialog
         fullScreen={fullScreen}
         open={!!modalData}
         isLoading={!modalData}
         onClose={onClose}
         content={
            <>
               <DialogTitle>
                  <Box sx={{ display: 'flex', gap: '7px', alignItems: 'center' }}>
                     <Typography fontSize="20px">
                        {modalData?.type === 'BUY'
                           ? t('PRODUCT_DETAILS:modals.buyNowTitle')
                           : t('PRODUCT_DETAILS:modals.addToCartTitle')}
                     </Typography>
                     {modalData?.data.quantityInSet !== 1 && (
                        <Tooltip
                           sx={{ m: '0 auto' }}
                           title={
                              t('PRODUCT_DETAILS:modals.multipleMustBeMore', {
                                 quantity: modalData?.data.quantityInSet,
                              }) || ''
                           }
                        >
                           <Error sx={{ width: '20px', height: '20px', color: 'red' }} />
                        </Tooltip>
                     )}
                  </Box>
               </DialogTitle>
               <FormikProvider value={formik}>{renderForm()}</FormikProvider>
               <DialogActions>
                  <OutlinedButton variant="outlined" onClick={onClose} disabled={isLoading}>
                     {t('TRANSLATION:cancel')}
                  </OutlinedButton>
                  <ContainedButton
                     type="submit"
                     onClick={() => (isBuyForm ? handleOnBuy(formikValues) : handleOnAddToCart(formikValues))}
                     variant="contained"
                     disabled={isLoading || !isValid}
                  >
                     {isBuyForm ? t('TRANSLATION:order') : t('TRANSLATION:addToCart')}
                  </ContainedButton>
               </DialogActions>
            </>
         }
      />
   )
}
